import { useEffect, useRef } from 'react';

export const useTreeInteraction = (containerRef, { zoom, pan, setZoom, setPan, zoomIn, zoomOut }) => {
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const startPanXRef = useRef(0);
  const startPanYRef = useRef(0);
  const panRef = useRef(pan);
  const mouseDownTargetRef = useRef(null);
  const dragDistanceRef = useRef(0);
  const DRAG_THRESHOLD = 4; // pixels before entering drag mode

  // Keep panRef in sync with current pan
  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Mouse wheel: Ctrl+Wheel to zoom, normal scroll to pan
    const handleWheel = (e) => {
      e.preventDefault();

      if (e.ctrlKey || e.metaKey) {
        // Ctrl+Wheel = Zoom
        if (e.deltaY > 0) {
          zoomOut();
        } else {
          zoomIn();
        }
      } else {
        // Normal scroll = Pan
        // Use the current pan value from ref to avoid stale closures
        const currentPan = panRef.current;

        // Shift+Scroll or Scroll with horizontal delta = Pan horizontally
        const isHorizontal = e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY);

        if (isHorizontal) {
          setPan({
            x: currentPan.x - (e.deltaX || e.deltaY),
            y: currentPan.y,
          });
        } else {
          // Scroll = Pan vertically
          setPan({
            x: currentPan.x,
            y: currentPan.y - e.deltaY,
          });
        }
      }
    };

    // Drag to pan: allow plain left-click-drag on empty canvas, or Ctrl/Cmd+click,
    // or middle-click. Use movement threshold to avoid accidental pans from slight
    // hand movements while clicking empty canvas to deselect.
    const handleMouseDown = (e) => {
      mouseDownTargetRef.current = e.target;
      dragDistanceRef.current = 0;

      // Check if mousedown is on a tree node element
      const isOnTreeNode = e.target.closest('[data-tree-node]');

      // Determine if this should start a potential pan:
      // - Middle-click on anything
      // - Ctrl/Cmd+left-click on anything
      // - Plain left-click on empty canvas (not on a node)
      const isMiddleClick = e.button === 1;
      const isCtrlClick = (e.ctrlKey || e.metaKey) && e.button === 0;
      const isLeftClickOnCanvas = e.button === 0 && !isOnTreeNode;

      if (isMiddleClick || isCtrlClick || isLeftClickOnCanvas) {
        startXRef.current = e.clientX;
        startYRef.current = e.clientY;
        startPanXRef.current = panRef.current.x;
        startPanYRef.current = panRef.current.y;
        // Don't set isDragging yet — wait for movement threshold
      }
    };

    const handleMouseMove = (e) => {
      // Only process move if we started in a pan-capable context
      const didMouseDownForPan = startXRef.current !== 0 || startYRef.current !== 0;
      if (!didMouseDownForPan) return;

      const dx = e.clientX - startXRef.current;
      const dy = e.clientY - startYRef.current;
      dragDistanceRef.current = Math.sqrt(dx * dx + dy * dy);

      // Enter drag mode once movement exceeds threshold
      if (dragDistanceRef.current > DRAG_THRESHOLD) {
        isDraggingRef.current = true;
      }

      // Apply pan if in drag mode
      if (isDraggingRef.current) {
        setPan({
          x: startPanXRef.current + dx,
          y: startPanYRef.current + dy,
        });
      }
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      startXRef.current = 0;
      startYRef.current = 0;
      dragDistanceRef.current = 0;
      mouseDownTargetRef.current = null;
    };

    // Keyboard shortcuts
    const handleKeyDown = (e) => {
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        zoomIn();
      } else if (e.key === '-') {
        e.preventDefault();
        zoomOut();
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomIn, zoomOut, setPan]);
};
